import { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import useApi from '@/hooks/useApi';
import useSign from '@/pages/SignLayout.context';
import Button from '@/components/Button';
import FormError from '@/components/FormError';
import FormVerifyCode from '@/components/FormVerifyCode';
import LoginGuardShell from './LoginGuardShell';
import styles from './Login.module.scss';

type SMSSudoLogin = {
  code: string;
};

const LoginPIN = () => {
  const { t } = useTranslation(['auth', 'common', 'sign']);
  const { workflowId } = useParams();

  const [loading, setLoading] = useState(false);
  const { setLoginSuccessful } = useSign();
  const [verifyAccess] = useApi('sign.verify');

  const [inputCode, setInputCode] = useState<string>('');

  const inputRef = useRef<HTMLInputElement>(null);

  const {
    setError,
    formState: { errors },
  } = useForm<SMSSudoLogin>();

  const verifyPhone = async (code: string) => {
    setLoading(true);
    const res = await verifyAccess({
      password: code,
      workflowId: workflowId ?? '',
    });
    const token = res?.response?.data.verificationToken;
    if (token) {
      sessionStorage.setItem('sudoToken', token);
      setLoginSuccessful();
    } else {
      setError('root', {
        message: t('auth:verifyPhone.errors.INVALID_CODE'),
      });
    }
    setLoading(false);
  };

  return (
    <LoginGuardShell introKey="sign:login.guard.pinIntro">
      <FormVerifyCode
        id="code"
        label={t('auth:verifyPhone.code')}
        ariaLabel={t('auth:verifyPhone.codePlaceholder')}
        onChange={(c: string) => setInputCode(c)}
        onComplete={verifyPhone}
        ref={inputRef}
        disabled={loading}
        value={inputCode}
      />
      {errors.root?.message && (
        <FormError isRoot message={errors.root.message} id="root-error" />
      )}
      <Button
        className={styles.fullButton}
        variant="primary"
        loading={loading}
        disabled={loading || inputCode.length < 6}
        onClick={() => void verifyPhone(inputCode)}
      >
        {t('sign:verifyPhone.verifyCode')}
      </Button>
    </LoginGuardShell>
  );
};

export default LoginPIN;
